<script>
import _ from 'lodash'
import baseMixin from '@/mixins/baseMixin'
import bookingFiltersMixin from '@/mixins/bookingFiltersMixin'
import BookingListTabKey from '@/shared/constants/booking/BookingListTabKey'
import { BookingFilters } from '@/shared/constants/booking/list/BookingFilters'
import CustomTooltip from '@/components/booking/bookingList/custom-tooltip.vue'
import BookingStatusLabelType from '@/shared/constants/booking/BookingStatusLabelType'
import FilterPanel from './filter-panel.vue'
import SearchableFilterPanel from './searchable-filter-panel.vue'
import StartEndTimeFilter from './start-end-time-filter.vue'

export default {
  name: 'FilterBookings',
  components: {
    CustomTooltip,
    SearchableFilterPanel,
    FilterPanel,
    StartEndTimeFilter,
  },
  mixins: [baseMixin, bookingFiltersMixin],
  props: {
    currentTabName: { type: String, required: true },
    locations: { type: Array, default: () => [] },
    educators: { type: Array, default: () => [] },
    classifications: { type: Array, default: () => [] },
    certifications: { type: Array, default: () => [] },
    grades: { type: Array, default: () => [] },
    replacing: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue', 'toggle', 'filter-change'],
  data() {
    return {
      showFilter: false,
      selectedFilter: 0,
      filterSearchText: '',
      filterScrollAreaHeight: 0,
      // Local editing state (before applying)
      localTime: null,
      localTime2: null,
      filterTime1: false,
      filterTime2: false,
      localSelectedStatuses: [],
      localSelectedEducators: [],
      localSelectedLocations: [],
      localSelectedClassifications: [],
      localSelectedCertifications: [],
      localSelectedGrades: [],
      localSelectedReplacing: [],
      BookingStatusLabelType,
      BookingListTabKey,
      statusItems: [
        { value: BookingStatusLabelType.Open.toLowerCase(), label: 'open' },
        {
          value: BookingStatusLabelType.Pending.toLowerCase(),
          label: 'pending',
        },
        {
          value: BookingStatusLabelType.Confirmed.toLowerCase(),
          label: 'confirmed',
        },
      ],
    }
  },
  computed: {
    activeFilterConfig() {
      return Object.values(BookingFilters).find(
        (f) => f.id === this.selectedFilter
      )
    },
    // only true for filters that define itemsProp & valueKey
    shouldShowPanel() {
      const cfg = this.activeFilterConfig
      return cfg && cfg.valueKey && cfg.labelKey && cfg.itemsProp
    },
    activeItems() {
      return this.shouldShowPanel ? this[this.activeFilterConfig.itemsProp] : []
    },
    activeSelected() {
      return this.shouldShowPanel
        ? this[this.activeFilterConfig.localSelectedProp]
        : []
    },
    filters() {
      if (!BookingFilters) return []

      return Object.values(BookingFilters)
        .filter((item) => item.id !== 0)
        .map((item) => ({
          id: item.id,
          label: this.$t(item.title),
          icon: item.icon,
        }))
    },
    filteredFilters() {
      const search = this.filterSearchText.toLowerCase()
      return this.filters.filter((f) => f.label.toLowerCase().includes(search))
    },
  },
  watch: {
    showFilter(val) {
      if (val) {
        this.$nextTick(() => {
          this.filterUpdateScrollAreaHeight()
        })
      }
    },
  },
  created() {
    this.filterUpdateScrollAreaHeight = _.throttle(
      this.filterUpdateScrollAreaHeight,
      100
    )
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutsideFilters, true)
    window.addEventListener('resize', this.filterUpdateScrollAreaHeight)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutsideFilters, true)
  },
  beforeDestroy() {
    document.removeEventListener(
      'resize',
      this.filterUpdateScrollAreaHeight,
      true
    )
  },
  methods: {
    handleClickOutsideFilters(e) {
      const refsToCheck = ['filterContainer', 'filterContainerBox']

      const path =
        typeof e.composedPath === 'function'
          ? e.composedPath()
          : (function fallbackPath(n) {
              const p = []
              while (n) {
                p.push(n)
                n = n.parentNode || n.host
              }
              p.push(window)
              return p
            })(e.target)

      const clickedInsideRef = refsToCheck.some((ref) => {
        const el = this.$refs[ref]
        return el && path.includes(el)
      })

      const clickedOnTrigger = path.some((node) => {
        if (!(node instanceof HTMLElement)) return false
        return node.closest('.filter-button')
      })

      const inOverlay = path.some(
        (node) =>
          node instanceof HTMLElement &&
          (node.closest('.v-menu__content') ||
            node.closest('.v-overlay__content'))
      )

      if (!clickedInsideRef && !clickedOnTrigger && !inOverlay) {
        this.showFilter = false
        this.selectedFilter = 0
      }
    },
    toggleAllFilter() {
      this.showFilter = !this.showFilter
      this.filterTime1 = false
      this.filterTime2 = false
      if (!this.showFilter) this.selectedFilter = 0

      if (this.showFilter) {
        // Sync local editing state from store
        this.localSelectedStatuses = [...this.filterStatuses]
        this.localSelectedEducators = [...this.filterEducators]
        this.localSelectedLocations = [...this.filterLocations]
        this.localSelectedClassifications = [...this.filterClassifications]
        this.localSelectedCertifications = [...this.filterCertifications]
        this.localSelectedGrades = [...this.filterGrades]
        this.localSelectedReplacing = [...this.filterReplacing]
        this.localTime = this.filterStartTime
        this.localTime2 = this.filterEndTime
      }
    },
    filterUpdateScrollAreaHeight() {
      this.$nextTick(() => {
        const filterContainerBoxArea =
          this.$refs.filterContainerBox ||
          (this.$refs.searchableFilterPanel &&
            this.$refs.searchableFilterPanel.$refs.filterContainerBox)

        if (!filterContainerBoxArea) return
        const marginBottom = 40

        const filterTopOffset = filterContainerBoxArea.getBoundingClientRect()
          .top
        const filterViewportHeight = window.innerHeight

        this.filterScrollAreaHeight =
          filterViewportHeight - filterTopOffset - marginBottom
      })
    },
    openSubPanel(id) {
      this.selectedFilter = id
      this.$nextTick(() => {
        this.filterUpdateScrollAreaHeight()
      })
    },
    backToMainList() {
      this.selectedFilter = 0
    },
    getFilterCount(id) {
      switch (id) {
        case BookingFilters.status.id:
          return this.filterStatuses.length
        case BookingFilters.educator.id:
          return this.filterEducators.length
        case BookingFilters.classification.id:
          return this.filterClassifications.length
        case BookingFilters.location.id:
          return this.filterLocations.length
        case BookingFilters.time.id:
          return this.filterStartTime && this.filterEndTime ? 1 : 0
        case BookingFilters.certification.id:
          return this.filterCertifications.length
        case BookingFilters.grade.id:
          return this.filterGrades.length
        case BookingFilters.replacing.id:
          return this.filterReplacing.length
        default:
          return 0
      }
    },
    clearFilter(id, toggleFilter = true) {
      switch (id) {
        case BookingFilters.default.id:
          this.resetAllFilters()

          this.localSelectedStatuses = []
          this.localSelectedEducators = []
          this.localSelectedLocations = []
          this.localSelectedClassifications = []
          this.localSelectedCertifications = []
          this.localSelectedGrades = []
          this.localSelectedReplacing = []
          this.localTime = null
          this.localTime2 = null
          break
        case BookingFilters.status.id:
          this.setStatuses([])
          this.localSelectedStatuses = []
          break
        case BookingFilters.educator.id:
          this.setEducators([])
          this.localSelectedEducators = []
          break
        case BookingFilters.classification.id:
          this.setClassifications([])
          this.localSelectedClassifications = []
          break
        case BookingFilters.location.id:
          this.setLocations([])
          this.localSelectedLocations = []
          break
        case BookingFilters.certification.id:
          this.setCertifications([])
          this.localSelectedCertifications = []
          break
        case BookingFilters.grade.id:
          this.setGrades([])
          this.localSelectedGrades = []
          break
        case BookingFilters.replacing.id:
          this.setReplacing([])
          this.localSelectedReplacing = []
          break
      }
      this.emitFilterChange(toggleFilter)
    },
    clearTime() {
      this.setTimeFilter({ startTime: null, endTime: null })
      this.localTime = null
      this.localTime2 = null
      this.emitFilterChange()
    },
    emitFilterChange(toggleFilter = true) {
      // Commit local state to store
      this.setStatuses([...this.localSelectedStatuses])
      this.setEducators([...this.localSelectedEducators])
      this.setLocations([...this.localSelectedLocations])
      this.setClassifications([...this.localSelectedClassifications])
      this.setCertifications([...this.localSelectedCertifications])
      this.setGrades([...this.localSelectedGrades])
      this.setReplacing([...this.localSelectedReplacing])
      this.setTimeFilter({
        startTime: this.localTime,
        endTime: this.localTime2,
      })

      if (toggleFilter) this.toggleAllFilter()

      this.$emit('filter-change', {
        statuses: this.filterStatuses,
        educators: this.filterEducators,
        locations: this.filterLocations,
        classifications: this.filterClassifications,
        certifications: this.filterCertifications,
        grades: this.filterGrades,
        replacing: this.filterReplacing,
        startTime: this.filterStartTime || null,
        endTime: this.filterEndTime || null,
        isAnyFilterActive: this.isAnyFilterActive,
      })
    },
    updateLocalSelected(val) {
      this[this.activeFilterConfig.localSelectedProp] = val
    },
  },
}
</script>

<template>
  <div ref="filterContainer" class="filter-button" style="position: relative">
    <CustomTooltip
      v-if="!isMobile && currentTabName !== BookingListTabKey.approvalRequired"
      position="bottom"
      :text="this.$t('topFilterBar.filter.title')"
    >
      <v-icon
        class="search-icon"
        :class="{ active: showFilter || isAnyFilterActive }"
        @click.stop="toggleAllFilter"
        >mdi-filter-variant</v-icon
      >
      <v-badge
        v-if="isAnyFilterActive"
        color="#00ACEF"
        dot
        overlap
        offset-x="8"
        offset-y="-18"
      ></v-badge>
    </CustomTooltip>

    <div
      v-if="isMobile && showFilter"
      class="filter-overlay"
      @click.stop="toggleAllFilter"
    ></div>

    <div
      v-if="showFilter"
      :class="[
        isMobile
          ? 'filter-container mobile-filter-container'
          : 'filter-container',
      ]"
    >
      <div v-if="isMobile" class="filter-mobile-header">
        <span class="header-handle"></span>
      </div>
      <div
        v-if="isMobile && selectedFilter === 0"
        class="px-4 mobile-filter-header d-flex align-center space-x-2"
      >
        <div class="d-flex">
          <v-icon size="20" @click.stop="toggleAllFilter"
            >mdi-arrow-left</v-icon
          >
          <div class="title">{{ this.$t('topFilterBar.filter.title') }}</div>
        </div>
        <div class="d-flex mobile-filter-close">
          <div
            v-if="isAnyFilterActive"
            class="cursor-pointer clear-filter clear-mobile-filter"
            :class="colorClass.text.primary400"
            @click="clearFilter(selectedFilter)"
          >
            {{ $t('topFilterBar.filter.clearAll') }}
          </div>
          <v-icon size="20" class="header-close" @click.stop="toggleAllFilter"
            >mdi-close</v-icon
          >
        </div>
      </div>

      <div class="filter-body">
        <SearchableFilterPanel
          v-if="selectedFilter === 0"
          ref="searchableFilterPanel"
          :title="$t('topFilterBar.filter.title')"
          :items="filteredFilters"
          :max-height="filterScrollAreaHeight"
          :is-any-filter-active="isAnyFilterActive"
          :search-key="'label'"
          :search-placeholder="$t('topFilterBar.filter.searchPlaceholder')"
          :get-filter-count="getFilterCount"
          @clear="clearFilter(selectedFilter)"
          @select="openSubPanel"
        />

        <!-- all filters except start / end time -->
        <FilterPanel
          v-if="shouldShowPanel"
          ref="filterPanel"
          :title="$t(activeFilterConfig.title)"
          :name="activeFilterConfig.name"
          :searchable="activeFilterConfig.isSearchable"
          :selected-filter="selectedFilter"
          :items="activeItems"
          :value-key="activeFilterConfig.valueKey"
          :label-key="activeFilterConfig.labelKey"
          :local-selected="activeSelected"
          :max-height="filterScrollAreaHeight"
          :selected-count="activeSelected.length"
          @update:localSelected="updateLocalSelected"
          @clear="clearFilter(selectedFilter)"
          @apply="emitFilterChange"
          @back="backToMainList"
        />

        <!-- start/end time -->
        <StartEndTimeFilter
          v-if="selectedFilter === 5"
          :local-time.sync="localTime"
          :local-time2.sync="localTime2"
          :filter-time1.sync="filterTime1"
          :filter-time2.sync="filterTime2"
          :filter-scroll-area-height="filterScrollAreaHeight"
          @back="backToMainList"
          @clear="clearTime"
          @apply="emitFilterChange"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/booking-top-filter-bar.scss';
</style>
